import { debounce, defer, each, zipObject } from 'lodash-es';
import * as yup from 'yup';
import Component, { forceArray } from 'navigation/component/Component';
import scroll from 'core/scroll';
import resize from 'helpers/resize';
import sizeStore from 'store/sizeStore';
import config from 'core/config';
import validators from './components/validators';
import AddressForm from './AddressForm';
const getFormParameters = (event) => {
    const formData = new FormData(event.currentTarget || event.target);
    if (event.submitter && event.submitter.name)
        formData.append(event.submitter.name, event.submitter.value);
    const urlParameters = '?' + [...formData.entries()]
        // @ts-ignore
        .map(x => `${encodeURIComponent(x[0])}=${encodeURIComponent(x[1])}`)
        .join('&');
    return {
        formData,
        urlParameters
    };
};
class Form extends Component {
    pageManager;
    preventDefault;
    submitted;
    method = 'POST';
    action = '/';
    inputs = [];
    checkboxes = [];
    validations = {};
    disabled = false;
    schema = yup.object().shape({});
    inputsByPath = {};
    submitCallback;
    name = 'Form';
    _loading = false;
    set loading(value) {
        this.el.classList.toggle('loading', value);
        this._loading = value;
    }
    get loading() {
        return this._loading;
    }
    constructor(el, { parent, pageManager }) {
        super(el);
        // this.pageManager = parent.pageManager
        this.preventDefault = true;
        this.submitted = false;
        defer(() => {
            this.pageManager =
                parent?.pageManager ||
                    parent?.options?.parent?.pageManager || pageManager;
            if (!this.pageManager) {
                if (parent.modules.content)
                    this.pageManager = parent.modules.content;
                else
                    throw new Error('Form must be a child of a Page');
            }
        });
        this.bindRefs();
        this.parseForm();
        this.quickValid();
    }
    getModulesMap() {
        return {
            addressForm: ['.address-form', AddressForm]
        };
    }
    parseForm() {
        this.method = (this.el.getAttribute('method') || 'POST').toUpperCase();
        this.action = this.el.getAttribute('action') || window.location.pathname;
        this.inputs = [...this.el.querySelectorAll('[name][data-validation], [name][data-required]')];
        this.checkboxes = [...this.el.querySelectorAll('[type="checkbox"][data-validation]')];
        this.inputsByPath = {};
        this.validations = this.inputs.reduce((memo, input) => {
            const required = input.hasAttribute('data-required');
            const validKey = input.getAttribute('data-validation');
            const name = this.formatName(input.name);
            let validator = validators[validKey];
            if (required)
                validator = validators.required(validator);
            if (validator)
                memo[name] = validator;
            if (!this.inputsByPath[name])
                this.inputsByPath[name] = [];
            this.inputsByPath[name].push(input);
            return memo;
        }, {});
        this.updateSchema(this.validations);
    }
    formatName(name) {
        return name.replace('[', '___').replace(']', '___');
    }
    updateSchema(validations) {
        this.schema = yup.object().shape(validations);
    }
    bindEvents(add = true) {
        const method = add ? 'addEventListener' : 'removeEventListener';
        this.el[method]('submit', this.onFormSubmit);
        this.el[method]('reset', this.onReset);
        this.inputs.forEach(input => {
            if (input.type === 'hidden') {
                input[method]('change', (e) => {
                    this.onInput(e);
                });
                return;
            }
            input[method]('input', this.onInput);
            input[method]('blur', this.onBlur);
        });
    }
    onReset = () => {
        this.setSubmitted.bind(false);
        this.inputs.forEach(input => {
            input.removeAttribute('value');
        });
    };
    onInput = (event) => {
        this.emit('input', event);
        const el = event.currentTarget;
        const isCapency = el.dataset.validation?.includes('capency');
        if (!isCapency)
            this.validateField(event.currentTarget.name, false);
        else
            this.debouncedValidateField(event.currentTarget.name, true, true);
        this.quickValid(isCapency);
        if (el.dataset.validation === 'phonecode') {
            this.validateField(event.currentTarget.name, false);
            this.validateField('phone', true);
        }
    };
    debouncedValidateField = debounce((path, addError, isCapency) => {
        const a = this.validateField(path, addError, isCapency);
        return a;
    }, 500);
    onBlur = (event) => {
        this.validateField(event.currentTarget.name, true, event.currentTarget.dataset.validation?.includes('capency'));
        this.quickValid();
    };
    validateField(path, addError = false, isCapency = false) {
        path = this.formatName(path);
        const inputs = this.inputsByPath[path];
        // @ts-ignore
        if (!~this.schema._nodes.indexOf(path))
            return;
        const values = this.getFormValues();
        if (isCapency) {
            inputs.forEach(i => {
                i.parentElement.parentElement.classList.add('loading-valid');
            });
        }
        this.schema.validateAt(path, values, { context: values })
            .then(() => {
            inputs.forEach(i => {
                // @ts-ignore
                i.parentNode.parentNode.style.setProperty('--form-error', '');
                // @ts-ignore
                i.parentNode.parentNode.style.setProperty('--error-height', '14px');
                i.parentNode.parentElement.classList.remove('error');
                i.parentElement.parentElement.classList.remove('loading-valid');
            });
        })
            .catch(({ errors }) => {
            errors = errors || [];
            const error = errors[0] || '';
            inputs.forEach(i => {
                i.parentElement.parentElement.classList.remove('loading-valid');
            });
            if (addError) {
                inputs.forEach(i => {
                    if (window.dataLayer) {
                        // @ts-ignore
                        const form = i.closest('form[data-name]');
                        window.dataLayer.push({
                            event: 'error_field',
                            field_error_type: error.trim() || 'empty',
                            field_name: i.getAttribute('name'),
                            //
                            form_name: form ? form.getAttribute('data-name') : 'none'
                        });
                    }
                    if (i.dataset.quiet)
                        return;
                    // @ts-ignore
                    i.parentNode.parentNode.style.setProperty('--form-error', this.getError(error));
                    // @ts-ignore
                    i.parentNode.parentNode.style.setProperty('--error-height', '14px');
                    i.parentNode.parentElement.classList.add('error');
                });
            }
        });
    }
    clear = () => {
        const form = this.el;
        form.reset();
    };
    quickValid(ignoreCapency = false) {
        const values = this.getFormValues();
        return this.schema.validate(values, {
            abortEarly: false,
            context: {
                ...values,
                ignoreCapency
            }
        })
            .then((e) => {
            this.emit('validate');
            this.el.classList.add('valid');
            this.el.classList.remove('not-valid');
            return true;
        })
            .catch(e => {
            this.emit('validate');
            this.el.classList.add('not-valid');
            this.el.classList.remove('valid');
            if (window.location.origin.includes('preprod.fursac.com') || window.location.origin.includes('period.paris')) {
                console.log('Not valid', {
                    ...e
                });
            }
            return false;
        });
    }
    getFormValues() {
        const formData = new FormData(this.el);
        const keys = [...formData.keys()];
        return zipObject(keys.map(k => this.formatName(k)), keys.map(k => formData.get(k)));
    }
    getAddressFormValues = (el) => {
        const values = this.getFormValues();
        const obj = {};
        Object.keys(values).reduce((_memo, key) => {
            const originalKey = key;
            if (key.includes(el.dataset.prefix))
                obj[key.replace(`${el.dataset.prefix}___`, '').replace('___', '')] = values[originalKey];
            return obj;
        }, {});
        return obj;
    };
    setSubmitted(submitted = true) {
        this.submitted = submitted;
        this.el.classList.toggle('submitted', submitted);
    }
    validateAddresses = async (values, event) => {
        const addressForms = forceArray(this.modules.addressForm).filter(Boolean).filter(addressForm => addressForm.status !== 'validated');
        if (addressForms.length) {
            this.loading = true;
            const toCheck = [];
            if (addressForms.length === 1)
                toCheck.push(addressForms[0]);
            if (addressForms.length > 1) {
                if (values.address_choice === 'new')
                    toCheck.push(addressForms.find((addressForm) => addressForm.el.dataset.prefix === 'delivery'));
                if (values.same_address === '0')
                    toCheck.push(addressForms.find((addressForm) => addressForm.el.dataset.prefix === 'billing'));
            }
            const areAddressesValid = await toCheck.reduce((previousAddressForm, addressForm) => {
                return previousAddressForm.then(() => {
                    return addressForm.verifyAddress();
                });
            }, Promise.resolve());
            if (areAddressesValid === false) {
                this.setSubmitted(false);
                event?.preventDefault();
                toCheck.find(addressForm => addressForm.status === 'pending_selection')?.el?.scrollIntoView({
                    behavior: 'smooth'
                });
                this.loading = false;
                return false;
            }
            else {
                return true;
            }
        }
    };
    onFormSubmit = async (event) => {
        if (this.el.dataset.bypass)
            return;
        if (this.submitted)
            return event.preventDefault();
        if (event.submitter && event.submitter.getAttribute('data-valid') === 'false')
            return this.submit(event);
        if (event.target.hasAttribute('data-direct'))
            return true;
        const values = this.getFormValues();
        try {
            if (this.preventDefault)
                event.preventDefault();
            await this.schema.validate(values, { abortEarly: false, context: values });
            const addressesValid = await this.validateAddresses(values, event);
            if (addressesValid === false) {
                this.setSubmitted(false);
            }
            else {
                this.setSubmitted(true);
                if (this.preventDefault)
                    this.submit(event);
            }
        }
        catch (e) {
            this.setSubmitted(false);
            event.preventDefault();
            if (e.errors || e.inner)
                return this.updateErrors(this.formatError(e));
            throw e;
        }
        finally {
            this.el.classList.add('submitted-once');
        }
        // .then(() => {
        // })
        // .catch(e => {
        //   if (e.errors || e.inner) return this.updateErrors(this.formatError(e))
        //   throw e
        // })
    };
    formatError(error) {
        console.log({ ...error });
        const errors = error.inner && error.inner.length ? error.inner : [Object.assign({}, error)];
        return errors.reduce((memo, error) => {
            if (!memo[error.path] && error.errors)
                memo[error.path] = error.errors[0];
            return memo;
        }, {});
    }
    updateErrors(errors) {
        let scrolled = false;
        each(this.inputsByPath, (inputs, path) => {
            let error = errors[path];
            inputs.forEach((i, k) => {
                if (!scrolled && !!error) {
                    if (i.type === 'hidden')
                        this.scrollTo(i.parentNode.parentElement);
                    else
                        this.scrollTo(i);
                    scrolled = true;
                }
                // @ts-ignore
                if (i.dataset.quiet) {
                    const validationType = `${i.dataset.validation}Quiet`;
                    error = error ? (config.errors[validationType] || error) : null;
                }
                i.parentNode.parentElement.style.setProperty('--form-error', this.getError(error));
                i.parentNode.parentElement.classList.toggle('error', !!error);
            });
        });
    }
    scrollTo(el) {
        if (scroll.locked())
            return;
        const inc = sizeStore.notificationHeight.get() + sizeStore.headerHeight.get();
        const top = el.getBoundingClientRect().top + scroll.scrollTop() - inc - 100;
        const s = Math.min(Math.max(0, top), document.scrollingElement.scrollHeight - resize.height());
        scroll.scrollTo(s);
    }
    submit(event) {
        if (this.disabled)
            return;
        const p = getFormParameters(event || { currentTarget: this.el });
        let action = this.action;
        if (event && event.submitter && event.submitter.hasAttribute('data-action'))
            action = event.submitter.getAttribute('data-action');
        if (this.method === 'GET')
            action += p.urlParameters;
        // this.el.classList.add('submitting')
        return Promise.resolve()
            .then(() => {
            if (this.submitCallback)
                return this.submitCallback(p.formData, p.urlParameters);
            // else this.el.classList.remove('submitting')
        })
            .then((prevent) => {
            // this.el.classList.remove('submitting')
            if (this.pageManager.state.loading || prevent === false)
                return;
            this.pageManager.once('loaded', () => {
                this.setSubmitted(false);
            });
            this.pageManager.virtual(action, {
                body: p.formData, method: this.method
            });
            this.emit('submitted');
        });
    }
    hide() {
        this.disabled = true;
    }
    setSubmitCallback(cb) {
        this.submitCallback = cb;
    }
    getError(errorId) {
        if (!errorId)
            return '';
        else
            return JSON.stringify(errorId);
    }
    flush() {
        this.inputs = null;
        this.schema = null;
        this.checkboxes = null;
    }
}
export { getFormParameters };
export default Form;
